// src/hooks/useScheduleFilters.js
import { useMemo } from 'react';

const asName = (x) => (typeof x === "string" ? x : x?.name || "");
const isRealEvent = (e) => !!e && !e._isFreeDay && !!e.event;

/**
 * Sprawdza czy przypisanie jest niepewne/nieznane
 */
const isUnknownAssignee = (s) => {
    try {
        if (!s) return true;
        if (typeof s === "object" && (s.unknown || s.tentative || s.isPlaceholder)) return true;

        const rawStr =
            typeof s === "string"
                ? s
                : typeof s?.name === "string"
                    ? s.name
                    : typeof s?.label === "string"
                        ? s.label
                        : "";

        const raw = String(rawStr || "").trim();
        if (!raw) return true;

        const lower = raw.toLowerCase();
        if (lower === "tbd" || lower === "?" || lower === "brak" || lower === "n/a") return true;

        if (/\(\s*\?\s*\)/.test(raw)) return true; // "Mateusz Kwella (?)"
        if (/^\?\s*/.test(raw)) return true; // "? Kowalski"
        if (/\s*\?$/.test(raw)) return true; // "Kowalski ?"
        if (raw.includes("?")) return true;

        return false;
    } catch {
        return false;
    }
};

/**
 * Hook do filtrowania eventów dla widoku
 */
export function useScheduleFilters({
    eventsByDate,
    monthDates,
    matchesView,
    selectedEmployee,
    tbdOnly,
    selectedEventNames,
    eventSearchQuery, // Dodajemy wyszukiwanie po tekście
}) {
    // Bazowe eventy po filtrach podstawowych
    const baseEventsForViewByDate = useMemo(() => {
        const map = {};
        const all = eventsByDate || {};

        (monthDates || []).forEach((date) => {
            let list = (all[date] || []).filter(isRealEvent).filter(matchesView);

            // Tylko z brakami (deficyt lub placeholdery)
            if (tbdOnly === true) {
                list = list.filter((ev) => {
                    const need = Number.isFinite(Number(ev?.needed))
                        ? Number(ev.needed)
                        : Math.max(1, (ev?.staff?.length || 0));
                    const have = ev?.staff?.length || 0;
                    const hasPlaceholder = (ev.staff || []).some(isUnknownAssignee);
                    return have < need || hasPlaceholder;
                });
            }

            // Filtr po pracowniku (w tym TBD/?/„(?)")
            if (selectedEmployee) {
                const ne = selectedEmployee.trim().toLowerCase();
                const isTbdAlias = ne === "tbd" || ne === "?" || ne === "brak" || ne === "n/a";
                if (isTbdAlias) {
                    list = list.filter((ev) => {
                        const need = Number.isFinite(Number(ev?.needed))
                            ? Number(ev.needed)
                            : Math.max(1, (ev?.staff?.length || 0));
                        const have = ev?.staff?.length || 0;
                        const hasPlaceholder = (ev.staff || []).some(isUnknownAssignee);
                        return have < need || hasPlaceholder;
                    });
                } else {
                    list = list.filter((ev) =>
                        (ev.staff || []).some(
                            (s) => (asName(s) || "").trim().toLowerCase() === ne
                        )
                    );
                }
            }

            if (list.length) map[date] = list;
        });

        return map;
    }, [eventsByDate, monthDates, matchesView, selectedEmployee, tbdOnly]);

    // Dodatkowy filtr po nazwach (multi-select) + wyszukiwanie tekstowe
    const eventsForViewByDate = useMemo(() => {
        let filtered = baseEventsForViewByDate;
        
        // Filtr po wybranych nazwach (multi-select)
        if (selectedEventNames && selectedEventNames.length > 0) {
            const choose = new Set(selectedEventNames.map((s) => (s || "").trim().toLowerCase()));
            const out = {};
            Object.entries(filtered).forEach(([date, list]) => {
                const picked = (list || []).filter((ev) =>
                    choose.has((ev?.event || "").trim().toLowerCase())
                );
                if (picked.length) out[date] = picked;
            });
            filtered = out;
        }
        
        // Filtr po wyszukiwaniu tekstowym (jeśli jest wpisane)
        if (eventSearchQuery && eventSearchQuery.trim()) {
            const searchQuery = eventSearchQuery.trim().toLowerCase();
            const out = {};
            Object.entries(filtered).forEach(([date, list]) => {
                const matched = (list || []).filter((ev) => {
                    const eventName = (ev?.event || "").trim().toLowerCase();
                    return eventName.includes(searchQuery);
                });
                if (matched.length) out[date] = matched;
            });
            filtered = out;
        }
        
        return filtered;
    }, [baseEventsForViewByDate, selectedEventNames, eventSearchQuery]);

    return { eventsForViewByDate };
}

/**
 * Hook do generowania list eventów do wyszukiwania
 */
export function useEventLists({
    eventsByDate,
    monthDates,
    matchesView,
    tbdOnly,
}) {
    const eventsAllNames = useMemo(() => {
        const names = new Set();
        const all = eventsByDate || {};
        (monthDates || []).forEach((date) => {
            (all[date] || [])
                .filter(isRealEvent)
                .filter(matchesView)
                .forEach((ev) => names.add(String(ev.event || "").trim()));
        });
        return Array.from(names).sort((a, b) => a.localeCompare(b, "pl", { sensitivity: "base" }));
    }, [eventsByDate, monthDates, matchesView]);

    const tbdEventNames = useMemo(() => {
        const tbd = new Set();
        const all = eventsByDate || {};
        (monthDates || []).forEach((date) => {
            (all[date] || [])
                .filter(isRealEvent)
                .filter(matchesView)
                .forEach((ev) => {
                    const need = Number.isFinite(Number(ev?.needed))
                        ? Number(ev.needed)
                        : Math.max(1, (ev?.staff?.length || 0));
                    const have = ev?.staff?.length || 0;
                    const hasPlaceholder = (ev.staff || []).some(isUnknownAssignee);
                    if (have < need || hasPlaceholder) tbd.add(String(ev.event || "").trim());
                });
        });
        return Array.from(tbd).sort((a, b) => a.localeCompare(b, "pl", { sensitivity: "base" }));
    }, [eventsByDate, monthDates, matchesView]);

    const eventsForSearch = useMemo(() => {
        if (!tbdOnly) return eventsAllNames;
        const norm = (s) => String(s || "").trim().toLowerCase();
        const tbdSet = new Set(tbdEventNames.map((s) => norm(s)));
        return eventsAllNames.filter((name) => tbdSet.has(norm(name)));
    }, [eventsAllNames, tbdEventNames, tbdOnly]);

    return { eventsAllNames, tbdEventNames, eventsForSearch };
}

